import { useMemo } from 'react';

import type { MentionType } from '@aline/common';
import type { PlateEditor } from 'platejs/react';
import {
  getOCRStatusFromFileVersion,
  isOcrProcessing,
} from 'src/editor/toolbars/right-panel/ai/shared/OCRStatusOrChildren';
import { Apollo } from 'src/gql/generated';

import type {
  DocumentMentionAccessResult,
  MentionAccessError,
  MentionEntityContextValue,
} from './useMentionAccessContext';
import { getIsMentionElement } from '../ChatPanel/ChatEditor/ChatEditorMention/utils';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface MentionEntityIds {
  documentIds: string[];
  playbookIds: string[];
  reportIds: string[];
  skillVersionIds: string[];
  taskIds: string[];
  workflowIds: string[];
}

export interface UseMentionEntitiesQueryResult {
  accessResultMap: Map<string, DocumentMentionAccessResult>;
  deletedDocumentIds: string[];
  hasDeletedMentions: boolean;
  hasInaccessibleMentions: boolean;
  hasProcessingDocuments: boolean;
  inaccessibleDocumentIds: string[];
  loading: boolean;
  mentionEntityContextValue: MentionEntityContextValue;
  processingDocumentIds: string[];
}

export function createEmptyMentionEntityIds(): MentionEntityIds {
  return {
    documentIds: [],
    playbookIds: [],
    reportIds: [],
    skillVersionIds: [],
    taskIds: [],
    workflowIds: [],
  };
}

export function extractMentionEntityIdsFromEditor(editor: PlateEditor | null): MentionEntityIds {
  const ids = createMentionEntityIdSets();
  if (!editor) return toMentionEntityIds(ids);

  const nodes = editor.api.nodes({
    at: [],
    match: n => getIsMentionElement(n),
  });

  for (const [node] of nodes) {
    if (!getIsMentionElement(node)) continue;

    switch (node.mentionTypeName) {
      case 'document':
        if (!node.isPlaceholder) ids.document.add(node.documentId);
        break;
      case 'selection':
      case 'selection-v2':
      case 'selection-v3':
        ids.document.add(node.documentId);
        break;
      case 'report':
        ids.report.add(node.reportId);
        break;
      case 'playbook':
        ids.playbook.add(node.playbookId);
        break;
      case 'task':
        ids.task.add(node.taskId);
        break;
      case 'workflow':
        ids.workflow.add(node.workflowId);
        break;
      case 'intake-form':
      case 'party':
      case 'report-column':
      case 'user':
        break;
    }
  }

  return toMentionEntityIds(ids);
}

/**
 * Extracts unique document IDs from `document` chips only, ignoring `selection`
 * and `selection-v2` mentions even though those carry a documentId too. Used by
 * UI that toggles full-document chips (e.g. the repository "Add to Chat" button),
 * where a selection mention must not be mistaken for a present document chip.
 */
export function extractDocumentChipIdsFromEditor(editor: PlateEditor | null): string[] {
  if (!editor) return [];

  const documentIds = new Set<string>();
  const nodes = editor.api.nodes({
    at: [],
    match: n => getIsMentionElement(n),
  });

  for (const [node] of nodes) {
    if (!getIsMentionElement(node)) continue;
    if (node.mentionTypeName !== 'document') continue;
    if (node.isPlaceholder) continue;
    documentIds.add(node.documentId);
  }

  return [...documentIds];
}

export function isValidUUID(id: string): boolean {
  return UUID_REGEX.test(id);
}

export function isMentionAccessError(result: { __typename: string }): result is MentionAccessError {
  return result.__typename === 'MentionAccessError';
}

export function isAccessibleDocument(
  result: DocumentMentionAccessResult
): result is Extract<DocumentMentionAccessResult, { __typename: 'Document' }> {
  return result.__typename === 'Document';
}

export function useMentionEntitiesQuery(
  mentionEntityIds: Partial<MentionEntityIds>
): UseMentionEntitiesQueryResult {
  const normalizedIds = useMemo(
    () => normalizeMentionEntityIds(mentionEntityIds),
    [mentionEntityIds]
  );
  const { invalidDocumentIds, validIds } = normalizedIds;
  const skip = getIsMentionEntityIdsEmpty(validIds);

  const { data, loading } = Apollo.useGetMentionEntitiesQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip,
    variables: { input: validIds },
  });

  return useMemo(() => {
    const accessResultMap = new Map<string, DocumentMentionAccessResult>();

    for (const id of invalidDocumentIds) {
      accessResultMap.set(id, {
        __typename: 'MentionAccessError',
        id,
        reason: Apollo.MentionAccessErrorReason.Deleted,
      });
    }

    const emptyContext = createMentionEntityContextValue(accessResultMap);

    if (skip) {
      return {
        accessResultMap,
        deletedDocumentIds: invalidDocumentIds,
        hasDeletedMentions: invalidDocumentIds.length > 0,
        hasInaccessibleMentions: false,
        hasProcessingDocuments: false,
        inaccessibleDocumentIds: [],
        loading: false,
        mentionEntityContextValue: emptyContext,
        processingDocumentIds: [],
      };
    }

    if (!data) {
      return {
        accessResultMap,
        deletedDocumentIds: invalidDocumentIds,
        hasDeletedMentions: invalidDocumentIds.length > 0,
        hasInaccessibleMentions: false,
        hasProcessingDocuments: false,
        inaccessibleDocumentIds: [],
        loading,
        mentionEntityContextValue: emptyContext,
        processingDocumentIds: [],
      };
    }

    const results = data.mentionEntities.documents;
    for (const result of results) {
      accessResultMap.set(result.id, result);
    }

    const processingDocumentIds = results
      .filter(
        (result): result is Extract<DocumentMentionAccessResult, { __typename: 'Document' }> => {
          if (!isAccessibleDocument(result)) return false;
          if (result.isSavePending) return true;
          const ocrStatus = result.latestVersion
            ? getOCRStatusFromFileVersion(result.latestVersion)
            : null;
          return ocrStatus !== null && isOcrProcessing(ocrStatus);
        }
      )
      .map(result => result.id);

    const deletedFromQuery = results
      .filter(
        (result): result is MentionAccessError =>
          isMentionAccessError(result) && result.reason === Apollo.MentionAccessErrorReason.Deleted
      )
      .map(result => result.id);
    const allDeletedIds = [...deletedFromQuery, ...invalidDocumentIds];
    const inaccessibleDocumentIds = results
      .filter(
        (result): result is MentionAccessError =>
          isMentionAccessError(result) && result.reason === Apollo.MentionAccessErrorReason.NoAccess
      )
      .map(result => result.id);

    return {
      accessResultMap,
      deletedDocumentIds: allDeletedIds,
      hasDeletedMentions: allDeletedIds.length > 0,
      hasInaccessibleMentions: inaccessibleDocumentIds.length > 0,
      hasProcessingDocuments: processingDocumentIds.length > 0,
      inaccessibleDocumentIds,
      loading: false,
      mentionEntityContextValue: createMentionEntityContextValue(accessResultMap, data),
      processingDocumentIds,
    };
  }, [data, invalidDocumentIds, loading, skip]);
}

function normalizeMentionEntityIds(mentionEntityIds: Partial<MentionEntityIds>): {
  invalidDocumentIds: string[];
  validIds: MentionEntityIds;
} {
  const invalidDocumentIds: string[] = [];

  return {
    invalidDocumentIds,
    validIds: {
      documentIds: filterValidIds(mentionEntityIds.documentIds ?? [], invalidDocumentIds),
      playbookIds: filterValidIds(mentionEntityIds.playbookIds ?? []),
      reportIds: filterValidIds(mentionEntityIds.reportIds ?? []),
      skillVersionIds: filterValidIds(mentionEntityIds.skillVersionIds ?? []),
      taskIds: filterValidIds(mentionEntityIds.taskIds ?? []),
      workflowIds: filterValidIds(mentionEntityIds.workflowIds ?? []),
    },
  };
}

function filterValidIds(ids: string[], invalidIds?: string[]): string[] {
  const validIds: string[] = [];
  for (const id of ids) {
    if (isValidUUID(id)) {
      validIds.push(id);
    } else {
      invalidIds?.push(id);
    }
  }
  return validIds;
}

function getIsMentionEntityIdsEmpty(ids: MentionEntityIds) {
  return (
    ids.documentIds.length === 0 &&
    ids.playbookIds.length === 0 &&
    ids.reportIds.length === 0 &&
    ids.skillVersionIds.length === 0 &&
    ids.taskIds.length === 0 &&
    ids.workflowIds.length === 0
  );
}

function createMentionEntityContextValue(
  accessResultMap: Map<string, DocumentMentionAccessResult>,
  data?: Apollo.GetMentionEntitiesQuery
): MentionEntityContextValue {
  return {
    documents: accessResultMap,
    playbooks: new Map(
      data?.mentionEntities.playbooks.map(playbook => [playbook.id, playbook]) ?? []
    ),
    reports: new Map(data?.mentionEntities.reports.map(report => [report.id, report]) ?? []),
    skillVersions: new Map(
      data?.mentionEntities.skillVersions.map(version => [version.id, version]) ?? []
    ),
    tasks: new Map(data?.mentionEntities.tasks.map(task => [task.id, task]) ?? []),
    workflows: new Map(
      data?.mentionEntities.workflows.map(workflow => [workflow.id, workflow]) ?? []
    ),
  };
}

function createMentionEntityIdSets(): Record<
  Extract<MentionType, 'document' | 'playbook' | 'report' | 'task' | 'workflow'>,
  Set<string>
> {
  return {
    document: new Set(),
    playbook: new Set(),
    report: new Set(),
    task: new Set(),
    workflow: new Set(),
  };
}

function toMentionEntityIds(
  ids: Record<
    Extract<MentionType, 'document' | 'playbook' | 'report' | 'task' | 'workflow'>,
    Set<string>
  >
): MentionEntityIds {
  return {
    documentIds: [...ids.document],
    playbookIds: [...ids.playbook],
    reportIds: [...ids.report],
    skillVersionIds: [],
    taskIds: [...ids.task],
    workflowIds: [...ids.workflow],
  };
}
